import React, { useMemo, useRef, useState } from 'react'
import { Formik, Form, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormGroup from '@mui/material/FormGroup'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import RadioGroupField from '@/components/formik/fields/RadioGroupField'
import * as yup from 'yup'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { Lock } from '@mui/icons-material'
import { UpdateEstablishmentDto } from '@/core/models/establishment.model'

export type SelectPatientPrivacyDialogProps = {
  open: boolean
  onSubmit: (values: Omit<UpdateEstablishmentDto, 'logo'>) => void
  onClose: () => void
  initialValues: Omit<UpdateEstablishmentDto, 'logo'>
  isUpdating?: boolean
}

function PrivacyDialog(props: SelectPatientPrivacyDialogProps) {
  const { open, onSubmit, onClose, isUpdating, initialValues } = props
  const { t } = useTranslation()
  const [selectedPro, setSelectedPro] = useState<string>('patients')
  const formRef = useRef<FormikProps<Omit<UpdateEstablishmentDto, 'logo'>>>(null)
  const [alignment, setAlignment] = useState<string>('patients')

  const selectPrivacyFormValidationSchema = yup.object().shape({
    patientsPrivacy: yup.string().required(),
  })

  const handleSelect = () => {
    formRef.current?.submitForm()
  }

  const patientsPrivacyOptions = useMemo(
    () => [
      { label: t(`page.admin.privacy.patients.options.medium`), value: 'medium' },
      { label: t(`page.admin.privacy.patients.options.hard`), value: 'hard' },
    ],
    [t]
  )

  const handleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
    setAlignment(newAlignment)
    setSelectedPro(newAlignment)
  }

  const message = useMemo(() => {
    if (selectedPro === 'patients') return t('page.admin.privacy.patients.message')
  }, [t, selectedPro])

  return (
    <Dialog onClose={onClose} maxWidth="md" open={open} PaperProps={{ sx: { minWidth: '700px' } }}>
      <DialogTitle>
        <Stack alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" gap="10px">
            <Typography variant="h6" component="div" sx={{ fontSize: '24px', textAlign: 'center' }}>
              {t('page.admin.privacy.title')}
            </Typography>
            <Lock />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Formik innerRef={formRef} initialValues={initialValues} onSubmit={onSubmit} validationSchema={selectPrivacyFormValidationSchema}>
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit}>
              <Stack my="20px">
                <ToggleButtonGroup fullWidth color="primary" value={alignment} exclusive onChange={handleChange}>
                  <ToggleButton fullWidth value="patients">
                    Patients
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="body1" component="div" sx={{ textAlign: 'justify' }}>
                  {message}
                </Typography>
                <FormGroup>
                  <Stack display={selectedPro === 'patients' ? 'block' : 'none'}>
                    <RadioGroupField fullWidth name="patientsPrivacy" options={patientsPrivacyOptions} variant="contained" />
                  </Stack>
                </FormGroup>
              </Stack>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions sx={{ p: '0 24px 16px' }}>
        <Button variant="neutral" onClick={onClose}>
          {t('common:button.cancel.label')}
        </Button>
        <LoadingButton color="primary" disableElevation loading={isUpdating} onClick={handleSelect} variant="contained">
          {t('common:button.confirm.label')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default PrivacyDialog
